import { z } from "zod";
import type { InvoiceLine, InvoiceStatus, PaymentMethodKind } from "./payments";

/**
 * A job statement: several of a job's invoices on one page, their lines grouped under each invoice's
 * number, and one payment for everything still owed. Amounts in cents. Live: balances are read when
 * it's opened, so an invoice paid some other way shows as paid.
 */

export const statementOptionsSchema = z.object({
  /** Each line's amount. */
  lineAmounts: z.boolean().default(true),
  /** Quantity × rate on lines that have them. */
  quantities: z.boolean().default(false),
  /** Each invoice's total, paid and balance under its lines. */
  invoiceTotals: z.boolean().default(true),
  /** Each invoice's date and due date by its number. */
  dates: z.boolean().default(true),
  /** A $0 line repeating one from an earlier invoice on the statement is left out. */
  hideRepeatedZero: z.boolean().default(true),
  memo: z.string().trim().max(2000).default(""),
  /** Card allowed for the whole amount, even over the company's card limit. */
  cardOverLimit: z.boolean().default(false),
});
export type StatementOptions = z.infer<typeof statementOptionsSchema>;

export const statementInputSchema = z.object({
  invoiceIds: z.array(z.string().uuid()).min(1, "Pick at least one invoice").max(50),
  options: statementOptionsSchema.default(() => statementOptionsSchema.parse({})),
});
export type StatementInput = z.infer<typeof statementInputSchema>;

/** One invoice as it goes in. */
export interface StatementSource {
  id: string;
  /** The number the customer knows. */
  number: string;
  title: string;
  date: string | null;
  dueDate: string | null;
  lines: InvoiceLine[];
  totalCents: number;
  paidCents: number;
  creditedCents: number;
  balanceCents: number;
  status: InvoiceStatus;
}

export interface StatementGroup extends StatementSource {
  /** Lines left out as repeated $0 lines. */
  hiddenLines: number;
}

export interface StatementBody {
  groups: StatementGroup[];
  totalCents: number;
  paidCents: number;
  creditedCents: number;
  balanceCents: number;
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();

/** Oldest invoice first (by date, then number); lines grouped under each; repeated $0 lines out. */
export function buildStatement(invoices: StatementSource[], opts: Pick<StatementOptions, "hideRepeatedZero">): StatementBody {
  const sorted = [...invoices].sort((a, b) => (a.date ?? "9999").localeCompare(b.date ?? "9999") || a.number.localeCompare(b.number, undefined, { numeric: true }));
  const seen = new Set<string>();
  const groups = sorted.map((i) => {
    const lines: InvoiceLine[] = [];
    let hiddenLines = 0;
    for (const l of i.lines) {
      const key = norm(l.description);
      if (opts.hideRepeatedZero && l.amountCents === 0 && seen.has(key)) hiddenLines++;
      else lines.push(l);
    }
    for (const l of i.lines) seen.add(norm(l.description));
    return { ...i, lines, hiddenLines };
  });
  const add = (k: "totalCents" | "paidCents" | "creditedCents" | "balanceCents") => groups.reduce((n, g) => n + g[k], 0);
  return { groups, totalCents: add("totalCents"), paidCents: add("paidCents"), creditedCents: add("creditedCents"), balanceCents: add("balanceCents") };
}

/**
 * Splits an amount (a card surcharge, a fee) across invoices in proportion to what each is paid,
 * to the cent: largest remainders get the leftover cents (ties: the earlier one).
 */
export function splitByShare(amountCents: number, shares: number[]): number[] {
  const total = shares.reduce((n, s) => n + s, 0);
  if (amountCents === 0 || total <= 0) return shares.map(() => 0);
  const exact = shares.map((s) => (amountCents * s) / total);
  const out = exact.map((x) => Math.floor(x));
  let left = amountCents - out.reduce((n, x) => n + x, 0);
  const order = exact.map((x, i) => ({ i, r: x - Math.floor(x) })).sort((a, b) => b.r - a.r || a.i - b.i);
  for (const { i } of order) {
    if (left <= 0) break;
    out[i]!++;
    left--;
  }
  return out;
}

/** What the customer sees from a statement link (and the preview before it's sent). */
export interface PublicStatement {
  title: string;
  date: string;
  company: { name: string; phone: string; email: string; logoUrl: string | null; primaryColor: string };
  customerName: string;
  address: string;
  options: StatementOptions;
  body: StatementBody;
  /** The invoices that can be paid here now (open, collectable) and their total. */
  payableCents: number;
  cardAllowed: boolean;
  cardLimitCents: number;
  surchargeBps: number;
  stripeKey: string | null;
  pending: { method: PaymentMethodKind; amountCents: number; at: string }[];
}

/** A statement on the job's Invoices tab. */
export interface StatementItem {
  id: string;
  invoiceNumbers: string[];
  balanceCents: number;
  totalCents: number;
  sentAt: string | null;
  viewedAt: string | null;
  paidAt: string | null;
  voided: boolean;
  createdAt: string;
  createdBy: string | null;
  /** Only right after it's made. */
  url?: string;
}
